import { useState, useRef, useEffect } from 'react';

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CustomDropdown({
  value,
  onChange,
  options,
  placeholder = "Select option",
  className = "",
  disabled = false
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  const displayValue = value || placeholder;
  const isPlaceholder = !value;

  return (
    <div 
      ref={dropdownRef}
      className={`relative ${className}`}
    >
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-5 py-4 rounded-full border border-purple-extra-light bg-white text-left flex items-center justify-between
          transition-all duration-200 cursor-pointer
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-purple-medium focus:border-purple-medium focus:outline-none'}
          ${isOpen ? 'border-purple-medium' : ''}
          ${isPlaceholder ? 'text-gray-400' : 'text-purple-medium'}
        `}
      >
        <span className="truncate">{displayValue}</span>
        <svg
          className={`w-5 h-5 text-purple-medium transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-60 overflow-auto">
          {options.map((option, index) => (
            <button
              key={option}
              type="button"
              onClick={() => handleOptionClick(option)}
              className={`
                w-full px-5 py-3 text-left hover:bg-purple-light hover:text-white
                transition-colors duration-150 cursor-pointer
                ${index === 0 ? 'rounded-t-2xl' : ''}
                ${index === options.length - 1 ? 'rounded-b-2xl' : ''}
                ${value === option ? 'bg-purple-medium text-white' : 'text-gray-700'}
              `}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}